import { useEffect, useRef, useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import QRCode from "qrcode";
import { useAuth } from "../auth/AuthProvider";
import { safeReturnTo, type LoginStep } from "../auth/authController";
import "../auth/login.css";

const headings: Record<LoginStep, string> = {
  signIn: "Sign in", totp: "Enter your code", setup: "Set up your authenticator",
  newPassword: "Choose a new password", recover: "Reset your password", resetPassword: "Check your email",
};
const descriptions: Record<LoginStep, string> = {
  signIn: "Manage your books, stories and media.",
  totp: "Enter the six-digit code from your authenticator app.",
  setup: "Add your account to an authenticator app, then enter its six-digit code.",
  newPassword: "Replace your temporary password to finish setting up your account.",
  recover: "Enter the email address for your admin account.",
  resetPassword: "If your account supports email recovery, a code will be sent. Enter it below with your new password.",
};

export function Login() {
  const auth = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState(auth.username);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [code, setCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [qr, setQr] = useState("");
  const [qrError, setQrError] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const error = useRef<HTMLDivElement>(null);
  const previousStep = useRef<LoginStep>(auth.step);
  const state = location.state as { returnTo?: unknown } | null;
  const returnTo = safeReturnTo(state?.returnTo);

  useEffect(() => {
    setPassword(""); setConfirmation(""); setCode(""); setShowPassword(false); setValidationError("");
    if (auth.username) setEmail(auth.username);
    if (previousStep.current !== auth.step) heading.current?.focus();
    previousStep.current = auth.step;
  }, [auth.step, auth.username]);
  useEffect(() => { if (auth.error || validationError) error.current?.focus(); }, [auth.error, validationError]);
  useEffect(() => {
    let active = true;
    setQr(""); setQrError(false);
    if (auth.setupUri) {
      void QRCode.toDataURL(auth.setupUri, { width: 224, margin: 3, color: { dark: "#1e3028", light: "#fefbf6" } })
        .then((value) => { if (active) setQr(value); })
        .catch(() => { if (active) setQrError(true); });
    }
    return () => { active = false; };
  }, [auth.setupUri]);

  if (auth.status === "signedIn") return <Navigate to={returnTo} replace />;
  const newPassword = auth.step === "newPassword" || auth.step === "resetPassword";
  const needsEmail = auth.step === "signIn" || auth.step === "recover" || (auth.step === "newPassword" && auth.missingAttributes.includes("email"));
  const needsCode = auth.step === "totp" || auth.step === "setup" || auth.step === "resetPassword";
  const busy = auth.busy || auth.status === "loading";
  const message = validationError || auth.error;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError("");
    if (newPassword && password !== confirmation) { setValidationError("Your passwords don't match. Please enter them again."); return; }
    const submittedPassword = password;
    const submittedCode = code;
    setPassword(""); setConfirmation(""); setCode(""); setShowPassword(false);
    switch (auth.step) {
      case "signIn": await auth.controller.signIn(email, submittedPassword); break;
      case "totp": case "setup": await auth.controller.confirm(submittedCode); break;
      case "newPassword": await auth.controller.confirm(submittedPassword, email); break;
      case "recover": await auth.controller.recover(email); break;
      case "resetPassword": await auth.controller.confirmReset(submittedCode, submittedPassword); break;
    }
  }
  const buttonText = auth.step === "signIn" ? "Sign in" : auth.step === "recover" ? "Send reset code" : auth.step === "resetPassword" ? "Update password" : "Continue";
  return (
    <div className="login-page">
      <a className="login-skip" href="#login-form">Skip to sign in</a>
      <aside className="login-art" aria-label="Ever & Up Books">
        <img src="/auth-assets/woodland.webp" width="1600" height="900" alt="" fetchPriority="high" />
        <div className="login-art-caption"><p>Growing with<br /> every page.</p></div>
      </aside>
      <main className="login-main">
        <div className="login-content">
          <a className="login-brand" href="https://everandupbooks.com" aria-label="Visit Ever & Up Books"><img src="/auth-assets/wordmark.png" width="1020" height="340" alt="Ever & Up Books" /></a>
          <div className="login-form-area" id="login-form" tabIndex={-1}>
            <p className="login-eyebrow">Admin workspace</p>
            <h1 ref={heading} tabIndex={-1}>{headings[auth.step]}</h1>
            <p className="login-description">{descriptions[auth.step]}</p>
            {message ? <div className="login-error" role="alert" ref={error} tabIndex={-1}>{message}</div> : null}
            {auth.notice ? <p className="login-notice" role="status">{auth.notice}</p> : null}
            <form onSubmit={(event) => void submit(event)} aria-label={headings[auth.step]} aria-busy={busy}>
              <fieldset disabled={busy}>
                {needsEmail ? <div className="login-field"><label htmlFor="login-email">Email address</label><input id="login-email" name="username" type="email" autoComplete="username" autoCapitalize="none" spellCheck={false} required value={email} onChange={(event) => setEmail(event.target.value)} /></div> : null}
                {auth.step === "setup" ? <div className="login-setup">
                  {qr ? <img className="login-qr" src={qr} width="224" height="224" alt="Scan this QR code with your authenticator app to add your CMS account." /> : <p role="status">{qrError ? "Use the setup key below to add your account manually." : "Preparing your setup code…"}</p>}
                  <label htmlFor="setup-key">Manual setup key</label><input id="setup-key" className="login-setup-key" readOnly autoComplete="off" spellCheck={false} value={auth.setupKey} onFocus={(event) => event.target.select()} />
                </div> : null}
                {needsCode ? <div className="login-field"><label htmlFor="login-code">{auth.step === "resetPassword" ? "Email verification code" : "Authenticator code"}</label><input id="login-code" name="code" className="login-code" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={12} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} /></div> : null}
                {auth.step === "signIn" || newPassword ? <div className="login-field">
                  <label htmlFor="login-password">{newPassword ? "New password" : "Password"}</label>
                  <div className="login-password-wrap"><input id="login-password" name="password" type={showPassword ? "text" : "password"} autoComplete={newPassword ? "new-password" : "current-password"} minLength={newPassword ? 12 : undefined} maxLength={256} required aria-describedby={newPassword ? "password-hint" : undefined} value={password} onChange={(event) => setPassword(event.target.value)} /><button className="login-password-toggle" type="button" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onClick={() => setShowPassword((value) => !value)}>{showPassword ? "Hide" : "Show"}</button></div>
                  {newPassword ? <p id="password-hint" className="login-hint">At least 12 characters, with uppercase and lowercase letters, a number and a symbol.</p> : null}
                </div> : null}
                {newPassword ? <div className="login-field"><label htmlFor="confirm-password">Confirm new password</label><input id="confirm-password" name="password-confirmation" type={showPassword ? "text" : "password"} autoComplete="new-password" minLength={12} maxLength={256} required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></div> : null}
                <button className="login-submit" type="submit">{busy ? "Please wait…" : buttonText}<span aria-hidden="true">→</span></button>
              </fieldset>
            </form>
            <div className="login-actions">
              {auth.step === "signIn" ? <button type="button" disabled={busy} onClick={() => auth.controller.showRecovery()}>Forgot your password?</button> : <button type="button" disabled={busy} onClick={() => void auth.controller.restart()}>Back to sign in</button>}
              {auth.step === "resetPassword" ? <button type="button" disabled={busy} onClick={() => void auth.controller.recover(auth.username)}>Send a new code</button> : null}
            </div>
            {auth.step === "totp" || auth.step === "setup" ? <p className="login-help">Lost access to your authenticator? Your account administrator can help you reset it.</p> : <p className="login-help">Admin access is provided by your account administrator.</p>}
          </div>
          <footer className="login-footer"><a href="https://everandupbooks.com">Back to the website <span aria-hidden="true">↗</span></a><span>Ever &amp; Up Books</span></footer>
        </div>
      </main>
    </div>
  );
}
